<template>
  <GenericCard
    :locked="locked"
    has-delete
    :on-delete="onCardDelete"
    :on-action="submitForm"
    :on-refresh="onCardRefresh"
    :invalid="$v.section.$invalid"
    :dirty="$v.section.$anyDirty"
    :create="create"
    title="Claim"
    :section="section"
    has-footer
    has-share
    :card-identifier="cardIdentifier"
  >
    <v-form
      :disabled="locked"
      @submit.prevent="submitForm"
    >
      <v-row>
        <v-col cols="12">
          <v-alert
            border="left"
            colored-border
            color="#2A3B4D"
            icon="mdi-information-outline"
            elevation="2"
            class="mb-6"
          >
            For each individualized harm, whether or not described cumulatively, add a new claim module
          </v-alert>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <h3>Claim</h3>
        </v-col>
      </v-row>
      <v-row>
        <v-col
          cols="12"
          sm="8"
          offset-sm="4"
        >
          <v-radio-group
            v-model="section.harm_type"
            row
          >
            <v-radio
              label="Past Harm"
              value="past_harm"
            />
            <v-radio
              label="Future Harm"
              value="future_harm"
            />
          </v-radio-group>
        </v-col>
      </v-row>
      <v-row>
        <v-col
          cols="3"
          sm="4"
          class="text-right"
        >
          <div>Concretely describe <br> feared or suffered</div>
        </v-col>
        <v-col
          cols="9"
          sm="8"
        >
          <v-textarea
            v-model="section.harm_description"
            auto-grow
            outlined
            autocomplete="off"
            label="Harm"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col
          cols="3"
          sm="4"
          class="text-right"
        >
          <div>Concretely describe <br> agent of harm</div>
        </v-col>
        <v-col
          cols="9"
          sm="8"
        >
          <v-textarea
            v-model="section.agent_description"
            auto-grow
            outlined
            autocomplete="off"
            label="Agent"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col
          cols="3"
          sm="4"
          class="text-right"
        >
          <div>Succinctly describe<br> motive and select all<br> applicable grounds</div>
        </v-col>
        <v-col
          cols="9"
          sm="8"
        >
          <v-textarea
            v-model="section.motive_description"
            auto-grow
            outlined
            autocomplete="off"
            label="Motive"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col
          cols="12"
          class="mb-3 text-right"
        >
          <div
            key="1"
            class="d-inline-block ml-4"
          >
            <v-checkbox
              v-model="section.basis_of_race"
              hide-details
            >
              <div slot="label">
                Race
              </div>
            </v-checkbox>
          </div>
          <div
            key="2"
            class="d-inline-block ml-4"
          >
            <v-checkbox
              v-model="section.basis_of_religion"
              hide-details
            >
              <div slot="label">
                Religion
              </div>
            </v-checkbox>
          </div>
          <div
            key="3"
            class="d-inline-block ml-4"
          >
            <v-checkbox
              v-model="section.basis_of_particular_social_group"
              hide-details
            >
              <div slot="label">
                Particular Social Group
              </div>
            </v-checkbox>
          </div>
          <div
            key="4"
            class="d-inline-block ml-4"
          >
            <v-checkbox
              v-model="section.basis_of_nationality"
              hide-details
            >
              <div slot="label">
                Nationality
              </div>
            </v-checkbox>
          </div>
          <div
            key="5"
            class="d-inline-block ml-4"
          >
            <v-checkbox
              v-model="section.basis_of_political_opinion"
              hide-details
            >
              <div slot="label">
                PO
              </div>
            </v-checkbox>
          </div>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <v-textarea
            v-model="section.narrative"
            auto-grow
            outlined
            autocomplete="off"
            label="Narrative"
          />
        </v-col>
      </v-row>
    </v-form>
  </GenericCard>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'
import { useVuelidate } from '@vuelidate/core'

import { PARTY_NAMESPACE } from '@/store/party'
import { alertHandler } from '@/alertHandler'

import GenericCard from '@/components/Party/PartyEdit/SectionCards/GenericCard'

export default {
  name: 'ClaimsCard',
  components: { GenericCard },

  props: {
    item: {
      type: Object,
      required: true,
    },
  },

  setup () {
    return {
      $v: useVuelidate(),
      ...alertHandler(),
    }
  },

  data () {
    return {
      section: {},
    }
  },

  validations () {
    return {
      section: {},
    }
  },

  computed: {
    cardIdentifier () { return `passport-card-${this.sectionIdentifier}` },
    create () { return this.item.id === null },

    locked: {
      get () {
        return this.peClaimsCardLock(this.sectionIdentifier)
      },

      set (value) {
        this.setPeClaimsCardLock({ index: this.sectionIdentifier, locked: value })
      },
    },

    sectionIdentifier () { return this.section.id || 'new' },

    subtitle () {
      if (this.create) return ''
      return `(${this.section.name})`
    },

    ...mapGetters(PARTY_NAMESPACE, [
      'peClaimsCardLock',
    ]),
  },

  watch: {
    item: {
      immediate: true,

      handler: function () {
        this.section = { ...this.item }
      },

      deep: true,
    },
  },

  created () {
    if (!this.create) this.initPeClaimsCard(this.sectionIdentifier)
  },

  methods: {
    getErrorMessages (validator) { return validator?.$errors.map(e => e.$message) },

    async onCardCreate () {
      return this.peClaimsCardCreate({ index: this.sectionIdentifier, section: this.section })
    },

    async onCardDelete () {
      if (this.create) return this.peClaimsCardCreateUndo()
      else {
        return this.peClaimsCardDelete({ id: this.section.id, index: this.sectionIdentifier })
      }
    },

    async onCardRefresh () {
      return this.peClaimsCardRefresh({ index: this.sectionIdentifier, section: this.section })
    },

    async onCardUpdate () {
      return this.peClaimsCardUpdate({ id: this.section.id, index: this.sectionIdentifier, section: this.section })
    },

    async onSectionUnlock () {
      return this.setPeClaimsCardLock({ index: this.sectionIdentifier, locked: false })
    },

    async submitForm () {
      if (this.locked) return this.onSectionUnlock()
      else if (this.create) return this.onCardCreate()
      else return this.onCardUpdate()
    },

    ...mapMutations(PARTY_NAMESPACE, [
      'peClaimsCardCreateUndo',
      'setPeClaimsCardLock',

    ]),

    ...mapActions(PARTY_NAMESPACE, [
      'initPeClaimsCard',
      'peClaimsCardCreate',
      'peClaimsCardDelete',
      'peClaimsCardRefresh',
      'peClaimsCardUpdate',
    ]),
  },
}
</script>
